import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Grid
} from '@mui/material';
import { APIHOST } from '../../common';
import { Conference } from '../../types/conference';

interface EditConferenceDialogProps {
  open: boolean;
  onClose: () => void;
  conference: Conference | null;
  onUpdate: () => void;
}

export default function EditConferenceDialog({ open, onClose, conference, onUpdate }: EditConferenceDialogProps) {
    console.debug(conference);
  const [editConferenceData, setEditConferenceData] = useState({
    name: '',
    longName: '',
    defaultArrivalDate: '',
    defaultDepartureDate: '',
    earliestArrivalDate: '',
    passcode: ''
  });
  const [editConferenceLoading, setEditConferenceLoading] = useState(false);
  const [editConferenceError, setEditConferenceError] = useState('');

  // Pre-populate form when dialog opens
  const handleOpen = () => {
    if (conference) {
      setEditConferenceData({
        name: conference.name || '',
        longName: conference.longName || '',
        defaultArrivalDate: conference.defaultArrivalDate || '',
        defaultDepartureDate: conference.defaultDepartureDate || '',
        earliestArrivalDate: conference.earliestArrivalDate || '',
        passcode: conference.passcode || ''
      });
    }
  };

  const handleUpdateConference = async () => {
    if (!conference) return;
    
    try {
      setEditConferenceLoading(true);
      setEditConferenceError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Validate required fields
      if (!editConferenceData.name || !editConferenceData.longName || 
          !editConferenceData.defaultArrivalDate || !editConferenceData.defaultDepartureDate ||
          !editConferenceData.earliestArrivalDate) {
        throw new Error('Please fill in all required fields');
      }

      // Validate dates
      const earliestArrival = new Date(editConferenceData.earliestArrivalDate);
      const defaultArrival = new Date(editConferenceData.defaultArrivalDate);
      const defaultDeparture = new Date(editConferenceData.defaultDepartureDate);

      if (earliestArrival > defaultArrival) {
        throw new Error('Earliest arrival date must be before default arrival date');
      }

      if (defaultArrival >= defaultDeparture) {
        throw new Error('Default departure date must be after default arrival date');
      }

      const response = await fetch(`${APIHOST}/api/conferences/${conference.documentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            name: editConferenceData.name,
            longName: editConferenceData.longName,
            defaultArrivalDate: editConferenceData.defaultArrivalDate,
            defaultDepartureDate: editConferenceData.defaultDepartureDate,
            earliestArrivalDate: editConferenceData.earliestArrivalDate,
            passcode: editConferenceData.passcode || undefined
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to update conference');
      }

      // Close dialog and refresh conference data
      handleClose();
      onUpdate();

    } catch (err) {
      setEditConferenceError(err instanceof Error ? err.message : 'Failed to update conference');
    } finally {
      setEditConferenceLoading(false);
    }
  };

  const handleClose = () => {
    setEditConferenceError('');
    setEditConferenceData({
      name: '',
      longName: '',
      defaultArrivalDate: '',
      defaultDepartureDate: '',
      earliestArrivalDate: '',
      passcode: ''
    });
    onClose();
  };

  // Pre-populate form when dialog opens
  if (open && conference && !editConferenceData.name) {
    handleOpen();
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Edit Conference Details</DialogTitle>
      <DialogContent>
        {editConferenceError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {editConferenceError}
          </Alert>
        )}
        
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Conference Name (Short)"
                value={editConferenceData.name}
                onChange={(e) => setEditConferenceData(prev => ({ ...prev, name: e.target.value }))}
                fullWidth
                required
                placeholder="e.g., CONF2025"
                helperText="Short identifier for the conference"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Conference Name (Full)"
                value={editConferenceData.longName}
                onChange={(e) => setEditConferenceData(prev => ({ ...prev, longName: e.target.value }))}
                fullWidth
                required
                placeholder="e.g., Annual Technology Conference 2025"
                helperText="Full display name"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Earliest Arrival Date"
                type="date"
                value={editConferenceData.earliestArrivalDate}
                onChange={(e) => setEditConferenceData(prev => ({ ...prev, earliestArrivalDate: e.target.value }))}
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Earliest date attendees can arrive"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Default Arrival Date"
                type="date"
                value={editConferenceData.defaultArrivalDate}
                onChange={(e) => setEditConferenceData(prev => ({ ...prev, defaultArrivalDate: e.target.value }))}
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Conference start date"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Default Departure Date"
                type="date"
                value={editConferenceData.defaultDepartureDate}
                onChange={(e) => setEditConferenceData(prev => ({ ...prev, defaultDepartureDate: e.target.value }))}
                fullWidth
                required
                InputLabelProps={{
                  shrink: true,
                }}
                helperText="Conference end date"
              />
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Access Passcode"
                value={editConferenceData.passcode}
                onChange={(e) => setEditConferenceData(prev => ({ ...prev, passcode: e.target.value }))}
                fullWidth
                placeholder="Optional passcode for registration"
                helperText="Leave empty if no passcode required"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={editConferenceLoading}>
          Cancel
        </Button>
        <Button 
          onClick={handleUpdateConference} 
          variant="contained" 
          disabled={editConferenceLoading || !editConferenceData.name || !editConferenceData.longName || 
                   !editConferenceData.defaultArrivalDate || !editConferenceData.defaultDepartureDate || 
                   !editConferenceData.earliestArrivalDate}
        >
          {editConferenceLoading ? <CircularProgress size={20} /> : 'Update Conference'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}